// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useState, useEffect } from 'react';
import {Modal} from 'ui'

import CollabModalTeam from './collab/CollabModalTeam'
import CollabModalInvite from './collab/CollabModalInvite'
import CollabModalActivate from './collab/CollabModalActivate'
import CollabInviteWrapper from './collab/CollabInviteWrapper'

import { useAddCollaborator, useAddSeat } from 'hooks/users/mutations'
import { useSubscription } from 'hooks/subscriptions/queries'
import { useCurrentUser, useProjectUsers } from 'hooks/users/queries'
import { useCurrentProject } from 'hooks/projects/useCurrentProject'
import { useProject } from 'hooks/projects/queries'
import { isOwner } from 'utils/subscriptionUtils'

const COLLAB_PAGE_TEAM = 'COLLAB_PAGE_TEAM'
const COLLAB_PAGE_INVITE = 'COLLAB_PAGE_INVITE'
const COLLAB_PAGE_ACTIVATE = 'COLLAB_PAGE_ACTIVATE'

function CollabModal(props) {
  const [page, setPage] = useState(COLLAB_PAGE_TEAM);
  const [collabText, setCollabText] = useState('');
  const [collabRole, setCollabRole] = useState('edit');
  const [sharingProject, setSharingProject] = useState(false);
  const [sharedEmail, setSharedEmail] = useState('');
  const [addingSeat, setAddingSeat] = useState(false);
  
  // Get current project data
  const { projectId } = useCurrentProject();
  const { data: project } = useProject(projectId);
  const { data: projectUsers, refetch } = useProjectUsers(projectId);
  
  const addCollaboratorMutation = useAddCollaborator();
  const addSeatMutation = useAddSeat();
  const { data: subscription } = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const subscriptionIsOwner = isOwner(subscription, currentUser?.id);
  
  // Extract data from projectUsers response
  const collaborators = projectUsers?.collaborators || [];
  const owner = projectUsers?.owner || null;
  const myRole = project?.role || null;
  
  // Refresh user data when modal opens
  useEffect(() => {
    if (props.show && projectId) {
      refetch();
    }
  }, [props.show, projectId, refetch]);
  
  const getShareErrorMessage = (error) => {
    if (!error) return null;
    
    if (error?.response?.status === 409) {
      return `${collabText} is already shared on this project.`;
    }
    
    return `Could not add ${collabText}`;
  };

  const hideCollabModal = () => {
    setPage(COLLAB_PAGE_TEAM);
    setCollabText('');
    setCollabRole('edit');
    setSharingProject(false);
    setSharedEmail('');
    setAddingSeat(false);

    addCollaboratorMutation.reset();
    addSeatMutation.reset();

    // Note: CashierActions.clearCashierErrors() will need to be handled elsewhere
    // or converted to a mutation if needed
    props.hideCollabModal();
  };

  const collabTextChange = (e) => {
    setCollabText(e.target.value);
  };

  const collabRoleChange = (role) => {
    setCollabRole(role);
  };

  const onAddSeat = (email) => {
    if (!subscription.id || !subscriptionIsOwner) return;

    setAddingSeat(true);
    
    addSeatMutation.mutate({
      subscriptionId: subscription.id,
      email,
      projectId: projectId // Pass projectId for cache invalidation
    }, {
      onSuccess: () => {
        setAddingSeat(false);
        setPage(COLLAB_PAGE_TEAM);
      },
      onError: () => {
        setAddingSeat(false);
        setPage(COLLAB_PAGE_TEAM); // Navigate back to team page to show error
      }
    });
  };

  const onActivateSubscription = (email) => {
    setSharingProject(false);
    setCollabText('');
    setPage(COLLAB_PAGE_ACTIVATE);
    setCollabRole('edit');
    setSharedEmail(email);
  };

  const onSubmit = () => {
    setSharingProject(true);
    
    addCollaboratorMutation.mutate({
      projectId: projectId,
      email: collabText,
      role: collabRole
    }, {
      onSuccess: (data) => {
        if (data.error) {
          setSharingProject(false);
          return;
        }

        const collaborators = data?.data.collaborators || [];

        // Find the added collaborator from the current collaborators list
        const collaborator = collaborators.find(c => c.email === collabText);
        
        if (
          (collaborator && collaborator.subscribed) || // collaborator is subscribed
          collabRole === 'view_only' ||
          !subscriptionIsOwner
        ) {
          setSharingProject(false);
          setCollabText('');
          setPage(COLLAB_PAGE_TEAM);
          setCollabRole('edit');
        } else {
          onActivateSubscription(collabText);
        }
      },
      onError: () => {
        setSharingProject(false);
      }
    });
  };

  const gotoInvitePage = () => {
    setPage(COLLAB_PAGE_INVITE);
  };

  const gotoTeamPage = () => {
    setPage(COLLAB_PAGE_TEAM);
    // Clear any errors from the mutations
    addCollaboratorMutation.reset();
    addSeatMutation.reset();
  };

  const getPage = () => {
    switch (page) {
      case COLLAB_PAGE_INVITE:
        return (<CollabInviteWrapper
          name={project?.name}
          nextPage={gotoInvitePage}
          isInvite={true}
          onSubmit={onSubmit}
          collabRoleChange={collabRoleChange}
          collabTextChange={collabTextChange}
          collabText={collabText}
          collabRole={collabRole}
          clickBack={gotoTeamPage}
          shareError={getShareErrorMessage(addCollaboratorMutation.error)}
          seatError={addSeatMutation.error?.message}
        >
          <CollabModalInvite
            projectID={projectId}
            myRole={myRole}
            name={project?.name}
            owner={owner}
            cancel={gotoTeamPage}
            sharing={sharingProject}
            />
        </CollabInviteWrapper>)
      case COLLAB_PAGE_ACTIVATE:
        return <CollabModalActivate
                email={sharedEmail}
                subscription={subscription}
                onAddSeat={onAddSeat}
                onSkip={gotoTeamPage}
                addingSeat={addingSeat}
              />
      default:
        return (<CollabInviteWrapper
                name={project?.name}
                nextPage={gotoInvitePage}
                isInvite={false}
                onSubmit={onSubmit}
                collabRoleChange={collabRoleChange}
                collabTextChange={collabTextChange}
                collabText={collabText}
                collabRole={collabRole}
                shareError={getShareErrorMessage(addCollaboratorMutation.error)}
                seatError={addSeatMutation.error ? 'An error occurred adding the user to your subscription.' : null}
              >
                <CollabModalTeam
                  projectID={projectId}
                  collaborators={collaborators}
                  myRole={myRole}
                  name={project?.name}
                  owner={owner}
                  nextPage={gotoInvitePage}
                  isSubscriptionOwner={subscriptionIsOwner}
                  onActivateSubscription={onActivateSubscription}
              />
          </CollabInviteWrapper>)
    }
  };

  const currentPage = getPage();

  return (
    <div>
        <style type="text/css">{`
        .custom-modal {width:500px !important;}
      `}</style>
      <Modal
        dialogClassName="custom-modal"
        show={props.show}
        onHide={hideCollabModal}
      >
        <Modal.Body>
            {currentPage}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CollabModal;
